import * as THREE from 'three';
import * as CANNON from 'cannon-es';

const GRAVITY = 26;
const THROW_SPEED = 14.5;
const FUSE_S = 2.1;
export const SHOCKWAVE_MAX = 2;
const BLAST_RADIUS = 5.2;
const KNOCK_FORCE = 16;
const LIFT = 7.5;

type LiveWave = {
  mesh: THREE.Mesh;
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  fuse: number;
  grounded: boolean;
};

type BlastFx = {
  mesh: THREE.Mesh;
  t: number;
  maxT: number;
};

export type ShockwaveBlast = {
  x: number;
  y: number;
  z: number;
  radius: number;
  force: number;
  lift: number;
};

/** Impulse grenade — knocks entities back without direct damage. */
export class FpsShockwave {
  private count = SHOCKWAVE_MAX;
  private live: LiveWave[] = [];
  private fx: BlastFx[] = [];
  private readonly geo = new THREE.SphereGeometry(0.11, 10, 10);
  private readonly mat = new THREE.MeshStandardMaterial({
    color: 0x4488ff,
    emissive: 0x2244aa,
    emissiveIntensity: 0.55,
    roughness: 0.35,
    metalness: 0.45,
  });

  constructor(
    private readonly sceneRoot: THREE.Group,
    private readonly world: CANNON.World,
  ) {}

  getCount(): number {
    return this.count;
  }

  addCount(n: number): number {
    const before = this.count;
    this.count = Math.min(SHOCKWAVE_MAX, this.count + n);
    return this.count - before;
  }

  tryThrow(origin: THREE.Vector3, dir: THREE.Vector3): boolean {
    if (this.count <= 0) return false;
    this.count -= 1;
    const mesh = new THREE.Mesh(this.geo, this.mat.clone());
    mesh.position.copy(origin).add(dir.clone().normalize().multiplyScalar(0.45));
    this.sceneRoot.add(mesh);
    const vel = dir.clone().normalize().multiplyScalar(THROW_SPEED);
    vel.y += 4;
    this.live.push({ mesh, pos: mesh.position.clone(), vel, fuse: FUSE_S, grounded: false });
    return true;
  }

  update(dt: number, onBlast: (b: ShockwaveBlast) => void): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const g = this.live[i]!;
      g.fuse -= dt;
      g.vel.y -= GRAVITY * dt;
      const next = g.pos.clone().add(g.vel.clone().multiplyScalar(dt));
      const from = new CANNON.Vec3(g.pos.x, g.pos.y, g.pos.z);
      const to = new CANNON.Vec3(next.x, next.y, next.z);
      const res = new CANNON.RaycastResult();
      const hit = this.world.raycastClosest(from, to, { skipBackfaces: true }, res);
      if (hit && res.hasHit && res.distance < g.pos.distanceTo(next) + 0.02) {
        g.pos.set(res.hitPointWorld.x, res.hitPointWorld.y + 0.1, res.hitPointWorld.z);
        g.grounded = true;
        g.vel.multiplyScalar(0.3);
      } else {
        g.pos.copy(next);
      }
      g.mesh.position.copy(g.pos);
      g.mesh.rotation.x += dt * 9;
      if (g.fuse <= 0 || (g.grounded && g.fuse < FUSE_S - 0.06)) {
        this.detonate(g, onBlast);
        this.live.splice(i, 1);
      }
    }
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i]!;
      f.t += dt;
      const u = Math.min(1, f.t / f.maxT);
      f.mesh.scale.setScalar(0.5 + u * BLAST_RADIUS * 2.4);
      (f.mesh.material as THREE.MeshBasicMaterial).opacity = 0.65 * (1 - u);
      if (f.t >= f.maxT) {
        f.mesh.removeFromParent();
        f.mesh.geometry.dispose();
        (f.mesh.material as THREE.Material).dispose();
        this.fx.splice(i, 1);
      }
    }
  }

  private detonate(g: LiveWave, onBlast: (b: ShockwaveBlast) => void): void {
    g.mesh.removeFromParent();
    (g.mesh.material as THREE.Material).dispose();
    const { x, y, z } = g.pos;
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.3, 1.2, 28),
      new THREE.MeshBasicMaterial({
        color: 0x66bbff,
        transparent: true,
        opacity: 0.72,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(x, y + 0.1, z);
    this.sceneRoot.add(ring);
    this.fx.push({ mesh: ring, t: 0, maxT: 0.42 });
    onBlast({ x, y, z, radius: BLAST_RADIUS, force: KNOCK_FORCE, lift: LIFT });
  }

  dispose(): void {
    for (const g of this.live) {
      g.mesh.removeFromParent();
      (g.mesh.material as THREE.Material).dispose();
    }
    this.live.length = 0;
    for (const f of this.fx) {
      f.mesh.removeFromParent();
      f.mesh.geometry.dispose();
      (f.mesh.material as THREE.Material).dispose();
    }
    this.fx.length = 0;
    this.geo.dispose();
    this.mat.dispose();
  }
}
